import { isCustom, isCustomFirmware } from "@/keyboard/action_utils";
import {
  KeyboardConfigInternal,
  ActionInternal,
} from "@/keyboard/internal_config";
import { isModifier } from "@/stores/keycap";
import {
  ActionInternalNested,
  isHoldTapInternal,
  isLayer,
  isMultipleActionsInternal,
  isMultipleKeyCodes,
} from ".";

export type Location = {
  layer: number;
  row: number;
  column: number;
};

export type Warning = {
  short: string;
  long: string;
  locations: Location[];
};

type IterFn = (act: ActionInternal, loc: Location) => void;

const forEachAction = (layers: ActionInternal[][][], fn: IterFn) => {
  layers.forEach((layer, li) =>
    layer.forEach((row, ri) =>
      row.forEach((act, ci) => {
        fn(act, {
          layer: li,
          row: ri,
          column: ci,
        });
      })
    )
  );
};

const forEachNestedAction = (
  act: ActionInternal,
  fn: (act: ActionInternalNested) => void
) => {
  if (isHoldTapInternal(act)) {
    fn(act.HoldTap.tap);
    fn(act.HoldTap.hold);
  } else if (isMultipleActionsInternal(act)) {
    act.MultipleActions.forEach((a) => fn(a));
  } else {
    fn(act);
  }
};

export const configWarnings = (config: KeyboardConfigInternal): Warning[] => {
  const warnings = [];
  let hasAllowBoot = false;

  forEachAction(config.layers, (action, loc) => {
    if (
      isHoldTapInternal(action) &&
      // assume that this works, but it's not guaranteed
      JSON.stringify(action.HoldTap.hold) == JSON.stringify(action.HoldTap.tap)
    ) {
      warnings.push({
        short: "Hold and Tap equal",
        long: "Hold and Tap variant of HoldTap action are the same. HoldTap can be removed.",
        locations: [loc],
      });
    }

    forEachNestedAction(action, (act) => {
      if (
        isCustom(act) &&
        isCustomFirmware(act.Custom) &&
        act.Custom.Firmware == "AllowBootloader"
      ) {
        hasAllowBoot = true;
      }

      if (isLayer(act) && loc.layer == act.Layer) {
        warnings.push({
          short: "Switch layer with no effect",
          long: `Layer(${act.Layer}) defined on layer ${loc.layer} has no effect.`,
          locations: [loc],
        });
      }

      const maxKeycodes = 6;
      if (
        isMultipleKeyCodes(act) &&
        act.MultipleKeyCodes.filter((kc) => !isModifier(kc)).length >
          maxKeycodes
      ) {
        warnings.push({
          short: `${maxKeycodes}-key-rollover`,
          long: `Currently maximum number of non-modifier keys pressed at the same time is ${maxKeycodes}.`,
          locations: [loc],
        });
      }
    });
  });

  if (!hasAllowBoot && config.bootload_strict) {
    warnings.push({
      short: "Secure mode without AllowBootloader",
      long:
        '"Bootload secure mode" in General settings is enabled but there is no "Allow Bootloader" action.' +
        " Normal firmware flashing will not be possible. It will require pressing the" +
        " hardware reset button for 3 seconds to switch to bootloader manually.",
      locations: [],
    });
  }

  return warnings;
};
